import React from 'react';
import {useSelector} from 'react-redux';
import moment from 'moment';
import cn from 'classnames';
import {
  RefundResultContainer,
  RefundResultItem,
  RefundResultItemRside,
  RefundResultItems
} from 'routes/App/Managers/RefundsCalculator/styles';
import {DashboardFields} from 'routes/App/dashboard/styles';
import {DashboardInner} from 'components/DashboardInner/styles';
import Body6 from 'components/typography/Body6';
import Subtitle5 from 'components/typography/Subtitle5';
import {REFUND_SOURCE} from 'helpers/constants/refundCalculator';
import {getReimbursementString} from 'helpers/functionUtils/refundHelper';

const getPrice = (item) => {
  return (item.quantity * item.subtotalAfterDiscount).toFixed(2);
};

const RCReimbursementResult = () => {
  const {orderData, reimbursements} = useSelector(
    (state) => state.refundsCalculator
  );

  return (
    <>
      <DashboardInner className='RefundsCalculator withoutShadow paddingLg'>
        <Subtitle5>Reimbursement details</Subtitle5>
        <DashboardFields className='noWrapDesktop AlignCenter marginMinus'>
          <RefundResultContainer>
            <Body6 className='fw700'>Order info</Body6>
            <RefundResultItems className='noBorder'>
              {orderData.items.map((orderItem) => (
                <RefundResultItem
                  modifierLevel={orderItem.path.length}
                  className={cn({
                    modifier: orderItem.path.length > 1
                  })}
                >
                  <Body6 className='list-title fw400'>
                    {orderItem.quantity > 1 && `${orderItem.quantity}X `}
                    {orderItem.name}
                  </Body6>
                  <RefundResultItemRside>
                    <Body6 className='rightBlock fw400'>
                      ${getPrice(orderItem)}
                    </Body6>
                  </RefundResultItemRside>
                </RefundResultItem>
              ))}
            </RefundResultItems>
            {orderData?.priceAdjustments?.length > 0 && (
              <RefundResultItems className='noBorder'>
                {orderData.priceAdjustments.map((adjust) => (
                  <RefundResultItem>
                    <Body6 className='list-title fw400'>
                      Adjustment for {adjust?.reason}
                    </Body6>
                    <RefundResultItemRside>
                      <Body6 className='rightBlock fw400'>
                        ${(adjust.totalAmount || 0).toFixed(2)}
                      </Body6>
                    </RefundResultItemRside>
                  </RefundResultItem>
                ))}
              </RefundResultItems>
            )}
            <RefundResultItems>
              <RefundResultItem>
                <Body6 className='list-title fw400'>Tip</Body6>
                <RefundResultItemRside>
                  <Body6 className='rightBlock fw400'>
                    ${(orderData.tip || 0).toFixed(2)}
                  </Body6>
                </RefundResultItemRside>
              </RefundResultItem>
              <RefundResultItem>
                <Body6 className='list-title fw400'>Delivery fee</Body6>
                <RefundResultItemRside>
                  <Body6 className='rightBlock fw400'>
                    ${(orderData.deliveryFee || 0).toFixed(2)}
                  </Body6>
                </RefundResultItemRside>
              </RefundResultItem>
            </RefundResultItems>
          </RefundResultContainer>
          {reimbursements.map((item) => (
            <>
              <RefundResultContainer>
                <Body6 className='fw700'>Reimbursement info</Body6>
                <RefundResultItems>
                  <RefundResultItem>
                    <Body6 className='list-title fw400'>
                      Reimbursement date
                    </Body6>
                    <RefundResultItemRside>
                      <Body6 className='rightBlock fw400'>
                        {moment(item.createdAt)
                          .utc()
                          .format('MM.DD.YY h:mm a') || 'N/A'}
                      </Body6>
                    </RefundResultItemRside>
                  </RefundResultItem>
                  <RefundResultItem>
                    <Body6 className='list-title fw400'>
                      Reimbursement fault
                    </Body6>
                    <RefundResultItemRside>
                      <Body6 className='rightBlock fw400'>
                        {item.responsibility || 'N/A'}
                      </Body6>
                    </RefundResultItemRside>
                  </RefundResultItem>
                  <RefundResultItem>
                    <Body6 className='list-title fw400'>Status</Body6>
                    <RefundResultItemRside>
                      <Body6 className='rightBlock fw400'>
                        {getReimbursementString(reimbursements) || 'N/A'}
                      </Body6>
                    </RefundResultItemRside>
                  </RefundResultItem>
                  <RefundResultItem>
                    <Body6 className='list-title fw400'>
                      Reimbursement type
                    </Body6>
                    <RefundResultItemRside>
                      <Body6 className='rightBlock fw400'>
                        {item.type === 'Fixed'
                          ? 'Fixed amount reimbursement'
                          : item.type || 'N/A'}
                      </Body6>
                    </RefundResultItemRside>
                  </RefundResultItem>
                  <RefundResultItem>
                    <Body6 className='list-title fw400'>
                      Reimbursement source
                    </Body6>
                    <RefundResultItemRside>
                      <Body6 className='rightBlock fw400'>
                        {REFUND_SOURCE[item.source] || 'N/A'}
                      </Body6>
                    </RefundResultItemRside>
                  </RefundResultItem>
                  {item.type === 'Partial' && (
                    <>
                      <RefundResultItems className='noBorder'>
                        {item?.refundedItems?.map((orderItem) => (
                          <RefundResultItem
                            modifierLevel={orderItem.path?.length}
                            className={cn({
                              modifier: orderItem.path?.length > 1
                            })}
                          >
                            <Body6 className='list-title minusWidth fw400'>
                              {orderItem.quantity > 1 &&
                                `${orderItem.quantity}X `}
                              {orderItem.name}
                            </Body6>
                            <RefundResultItemRside>
                              <Body6 className='rightBlock fw400'>
                                ${getPrice(orderItem)}
                              </Body6>
                            </RefundResultItemRside>
                          </RefundResultItem>
                        ))}
                      </RefundResultItems>
                    </>
                  )}
                  <RefundResultItem>
                    <Body6 className='list-title fw400'>
                      Reimbursement amount
                    </Body6>
                    <RefundResultItemRside>
                      <Body6 className='rightBlock fw400'>
                        ${item.totalLocationTransferAmount.toFixed(2) || 'N/A'}
                      </Body6>
                    </RefundResultItemRside>
                  </RefundResultItem>
                  <RefundResultItem>
                    <Body6 className='list-title fw400'>Transfer ID</Body6>
                    <RefundResultItemRside>
                      <Body6 className='rightBlock fw400'>
                        {item.transferId || 'N/A'}
                      </Body6>
                    </RefundResultItemRside>
                  </RefundResultItem>
                </RefundResultItems>
              </RefundResultContainer>
            </>
          ))}
        </DashboardFields>
      </DashboardInner>
    </>
  );
};

export default RCReimbursementResult;
